// src/pages/pmc/PmcAgencyRegistrationsPane.js

import { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebaseConfig";
import toast from "react-hot-toast";
import dayjs from "dayjs";

const cx = (...xs) => xs.filter(Boolean).join(" ");

const OFFICE_TYPE_LABELS = {
  BANK: "Bank / Financing Agency",
  DISTRICT_REG: "District Registrar Office",
  STATE_REG: "State Registrar Office",
  CENTRAL_AGENCY: "Central Agency",
  NODAL_AGENCY: "Nodal Agency",
};

const OFFICE_TYPE_OPTIONS = [
  { value: "BANK", label: "Bank / Financing Agency" },
  { value: "DISTRICT_REG", label: "District Registrar" },
  { value: "STATE_REG", label: "State Registrar" },
  { value: "CENTRAL_AGENCY", label: "Central Agency" },
  { value: "NODAL_AGENCY", label: "Nodal Agency" },
];

const STATUS_COLORS = {
  approved: "green",
  pending: "yellow",
  rejected: "red",
};

function Badge({ color = "gray", children }) {
  const map = {
    green: "bg-emerald-100 text-emerald-700 border-emerald-200",
    yellow: "bg-amber-100 text-amber-700 border-amber-200",
    red: "bg-red-100 text-red-700 border-red-200",
    gray: "bg-gray-100 text-gray-700 border-gray-200",
    blue: "bg-sky-100 text-sky-700 border-sky-200",
    purple: "bg-violet-100 text-violet-700 border-violet-200",
    dark: "bg-slate-900 text-white border-slate-900",
  };

  return (
    <span
      className={cx(
        "inline-flex items-center rounded-full border px-2.5 py-1 text-[10px] font-black",
        map[color] || map.gray
      )}
    >
      {children}
    </span>
  );
}

function StatCard({ label, value, note, tone = "emerald" }) {
  const toneMap = {
    emerald: "from-emerald-500 to-teal-600",
    blue: "from-sky-500 to-blue-600",
    amber: "from-amber-400 to-orange-500",
    red: "from-red-500 to-rose-600",
    purple: "from-violet-500 to-purple-600",
    slate: "from-slate-700 to-slate-950",
  };

  return (
    <div className="overflow-hidden rounded-2xl border border-white/70 bg-white shadow-sm">
      <div className={cx("h-1 bg-gradient-to-r", toneMap[tone] || toneMap.emerald)} />
      <div className="p-4">
        <div className="text-[11px] font-black uppercase tracking-wide text-slate-500">
          {label}
        </div>
        <div className="mt-1 text-2xl font-black text-slate-900">{value}</div>
        {note && <div className="mt-1 text-[11px] text-slate-500">{note}</div>}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[11px] font-bold text-slate-600">{label}</div>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function parseDistricts(v = "") {
  return String(v || "")
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);
}

function joinDistricts(arr = []) {
  return Array.isArray(arr) ? arr.join(", ") : "";
}

export default function AgencyRegistrationsPane() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [lockFilter, setLockFilter] = useState("all");
  const [q, setQ] = useState("");

  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [processingId, setProcessingId] = useState("");

  const load = async () => {
    try {
      setLoading(true);

      const snap = await getDocs(collection(db, "portal_users"));
      const list = [];

      snap.forEach((d) => list.push({ id: d.id, ...d.data() }));

      list.sort(
        (a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)
      );

      setRows(list);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load registrations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    let list = [...rows];

    if (statusFilter !== "all") {
      list = list.filter((r) => (r.status || "pending") === statusFilter);
    }

    if (typeFilter !== "all") {
      list = list.filter((r) => (r.officeType || "") === typeFilter);
    }

    if (lockFilter === "locked") {
      list = list.filter((r) => !!r.locked);
    }

    if (lockFilter === "unlocked") {
      list = list.filter((r) => !r.locked);
    }

    const needle = q.trim().toLowerCase();

    if (needle) {
      list = list.filter((r) => {
        const hay = [
          r.officeName,
          r.fullName,
          r.email,
          r.phone,
          r.state,
          r.officeType,
          r.centralAgencyName,
          r.adminRemark,
          joinDistricts(r.requestedDistricts),
          joinDistricts(r.assignedDistricts),
        ]
          .map((x) => String(x || "").toLowerCase())
          .join("|");

        return hay.includes(needle);
      });
    }

    return list;
  }, [rows, statusFilter, typeFilter, lockFilter, q]);

  const totals = useMemo(() => {
    return {
      total: rows.length,
      pending: rows.filter((r) => (r.status || "pending") === "pending").length,
      approved: rows.filter((r) => r.status === "approved").length,
      rejected: rows.filter((r) => r.status === "rejected").length,
      locked: rows.filter((r) => !!r.locked).length,
    };
  }, [rows]);

  const updateLocalRow = (id, patch) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const updateDistricts = async (row) => {
    const current = joinDistricts(row.assignedDistricts);
    const next = window.prompt("Assigned Districts (comma separated):", current);

    if (next === null) return;

    const list = parseDistricts(next);

    try {
      setProcessingId(row.id);

      await updateDoc(doc(db, "portal_users", row.id), {
        assignedDistricts: list,
        updatedAt: serverTimestamp(),
      });

      updateLocalRow(row.id, { assignedDistricts: list });
      toast.success("Assigned districts updated");
    } catch (e) {
      console.error(e);
      toast.error("Failed to update districts");
    } finally {
      setProcessingId("");
    }
  };

  const approve = async (row) => {
    if (row.locked) {
      toast.error("Locked user ko approve nahi kar sakte. Pehle unlock karo.");
      return;
    }

    try {
      setProcessingId(row.id);

      const assigned =
        row.assignedDistricts && row.assignedDistricts.length
          ? row.assignedDistricts
          : row.requestedDistricts || [];

      const patch = {
        status: "approved",
        assignedDistricts: assigned,
        approvedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await updateDoc(doc(db, "portal_users", row.id), patch);

      updateLocalRow(row.id, {
        status: "approved",
        assignedDistricts: assigned,
      });

      toast.success("Approved");
    } catch (e) {
      console.error(e);
      toast.error("Approve failed");
    } finally {
      setProcessingId("");
    }
  };

  const reject = async (row) => {
    const reason =
      window.prompt("Reject reason (optional):", row.adminRemark || "") || "";

    try {
      setProcessingId(row.id);

      const patch = {
        status: "rejected",
        adminRemark: reason,
        updatedAt: serverTimestamp(),
      };

      await updateDoc(doc(db, "portal_users", row.id), patch);

      updateLocalRow(row.id, {
        status: "rejected",
        adminRemark: reason,
      });

      toast.success("Rejected");
    } catch (e) {
      console.error(e);
      toast.error("Reject failed");
    } finally {
      setProcessingId("");
    }
  };

  const toggleLock = async (row) => {
    const nextLocked = !row.locked;

    const ok = window.confirm(
      nextLocked
        ? `Lock this user?\n\n${row.email || row.officeName || row.id}\n\nLocked user stakeholder dashboard access block kar sakta hai.`
        : `Unlock this user?\n\n${row.email || row.officeName || row.id}`
    );

    if (!ok) return;

    try {
      setProcessingId(row.id);

      const patch = {
        locked: nextLocked,
        lockedAt: nextLocked ? serverTimestamp() : null,
        unlockedAt: nextLocked ? null : serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await updateDoc(doc(db, "portal_users", row.id), patch);

      updateLocalRow(row.id, {
        locked: nextLocked,
      });

      toast.success(nextLocked ? "User locked" : "User unlocked");
    } catch (e) {
      console.error(e);
      toast.error("Lock update failed");
    } finally {
      setProcessingId("");
    }
  };

  const handleDelete = async (row) => {
    const ok1 = window.confirm(
      `Permanent delete?\n\nOffice: ${row.officeName || "—"}\nEmail: ${
        row.email || "—"
      }\n\nYe portal user permanently delete ho jayega.`
    );

    if (!ok1) return;

    const ok2 = window.confirm("Final confirmation: delete ke baad restore nahi hoga.");
    if (!ok2) return;

    try {
      setProcessingId(row.id);

      await deleteDoc(doc(db, "portal_users", row.id));

      setRows((prev) => prev.filter((r) => r.id !== row.id));
      toast.success("Registration permanently deleted");
    } catch (e) {
      console.error(e);
      toast.error("Delete failed. Firestore rules check karo.");
    } finally {
      setProcessingId("");
    }
  };

  const openEdit = (row) => {
    setEditing(row);
    setEditForm({
      officeName: row.officeName || "",
      officeType: row.officeType || "",
      centralAgencyName: row.centralAgencyName || "",
      fullName: row.fullName || "",
      email: row.email || "",
      phone: row.phone || "",
      state: row.state || "Gujarat",
      requestedDistrictsText: joinDistricts(row.requestedDistricts),
      assignedDistrictsText: joinDistricts(row.assignedDistricts),
      status: row.status || "pending",
      adminRemark: row.adminRemark || "",
      locked: !!row.locked,
    });
  };

  const closeEdit = () => {
    if (savingEdit) return;
    setEditing(null);
    setEditForm(null);
  };

  const updateEditField = (field, value) => {
    setEditForm((prev) => ({ ...(prev || {}), [field]: value }));
  };

  const saveEdit = async (e) => {
    e.preventDefault();

    if (!editing || !editForm) return;

    if (!editForm.officeName || !editForm.email) {
      toast.error("Office name and email mandatory hai.");
      return;
    }

    try {
      setSavingEdit(true);

      const patch = {
        officeName: editForm.officeName.trim(),
        officeType: editForm.officeType || "",
        centralAgencyName: editForm.centralAgencyName.trim(),
        fullName: editForm.fullName.trim(),
        email: editForm.email.trim().toLowerCase(),
        phone: editForm.phone.trim(),
        state: editForm.state.trim(),
        requestedDistricts: parseDistricts(editForm.requestedDistrictsText),
        assignedDistricts: parseDistricts(editForm.assignedDistrictsText),
        status: editForm.status || "pending",
        adminRemark: editForm.adminRemark.trim(),
        locked: !!editForm.locked,
        updatedAt: serverTimestamp(),
      };

      await updateDoc(doc(db, "portal_users", editing.id), patch);

      updateLocalRow(editing.id, patch);

      toast.success("Registration updated");
      closeEdit();
    } catch (e2) {
      console.error(e2);
      toast.error("Update failed");
    } finally {
      setSavingEdit(false);
    }
  };
    return (
    <div className="space-y-5">
      {/* HEADER */}
      <div className="rounded-3xl bg-gradient-to-r from-slate-950 via-emerald-950 to-teal-800 p-5 text-white shadow-xl">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="text-[11px] font-black uppercase tracking-[0.22em] text-emerald-300">
              Stakeholder Access Control
            </div>

            <h2 className="mt-2 text-2xl font-black">
              Agency Registration Control Room
            </h2>

            <p className="mt-1 text-sm text-emerald-50">
              Bank, Registrar, Nodal Agency અને Central Agency users ને approve, edit, lock અથવા delete કરો.
            </p>
          </div>

          <button
            type="button"
            onClick={load}
            className="rounded-full bg-white px-4 py-2 text-xs font-black text-emerald-800 shadow hover:bg-emerald-50"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* STATS */}
      <div className="grid gap-3 md:grid-cols-5">
        <StatCard label="Total" value={totals.total} note="All registrations" tone="emerald" />
        <StatCard label="Pending" value={totals.pending} note="Approval pending" tone="amber" />
        <StatCard label="Approved" value={totals.approved} note="Active users" tone="blue" />
        <StatCard label="Rejected" value={totals.rejected} note="Rejected users" tone="red" />
        <StatCard label="Locked" value={totals.locked} note="Access blocked" tone="slate" />
      </div>

      {/* FILTERS */}
      <div className="rounded-3xl bg-emerald-50 border border-emerald-100 p-4 grid md:grid-cols-4 gap-3 text-xs">
        <div>
          <div className="text-[11px] font-semibold text-slate-600">Status</div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="mt-1 w-full px-3 py-2 border rounded-xl bg-white text-xs"
          >
            <option value="all">All</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>

        <div>
          <div className="text-[11px] font-semibold text-slate-600">Office Type</div>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="mt-1 w-full px-3 py-2 border rounded-xl bg-white text-xs"
          >
            <option value="all">All</option>
            {OFFICE_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        <div>
          <div className="text-[11px] font-semibold text-slate-600">Lock Status</div>
          <select
            value={lockFilter}
            onChange={(e) => setLockFilter(e.target.value)}
            className="mt-1 w-full px-3 py-2 border rounded-xl bg-white text-xs"
          >
            <option value="all">All</option>
            <option value="unlocked">Unlocked</option>
            <option value="locked">Locked</option>
          </select>
        </div>

        <div>
          <div className="text-[11px] font-semibold text-slate-600">Search</div>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Office / email / district..."
            className="mt-1 w-full px-3 py-2 border rounded-xl bg-white text-xs"
          />
        </div>
      </div>

      {/* TABLE */}
      <div className="rounded-3xl bg-white border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="px-4 py-8 text-xs text-slate-500 text-center">
            Loading registrations…
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-4 py-8 text-xs text-slate-500 text-center">
            No registrations match current filters.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-slate-950 text-white">
                <tr>
                  <th className="px-3 py-3 text-left w-8">#</th>
                  <th className="px-3 py-3 text-left">Office</th>
                  <th className="px-3 py-3 text-left">Contact</th>
                  <th className="px-3 py-3 text-left">Type</th>
                  <th className="px-3 py-3 text-left">Requested</th>
                  <th className="px-3 py-3 text-left">Assigned</th>
                  <th className="px-3 py-3 text-center">Status</th>
                  <th className="px-3 py-3 text-center">Lock</th>
                  <th className="px-3 py-3 text-center">Actions</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100">
                {filtered.map((r, idx) => {
                  const status = r.status || "pending";
                  const color = STATUS_COLORS[status] || "gray";
                  const typeLabel = OFFICE_TYPE_LABELS[r.officeType] || r.officeType || "—";
                  const isBusy = processingId === r.id;

                  return (
                    <tr
                      key={r.id}
                      className={cx(
                        "hover:bg-emerald-50/50",
                        idx % 2 === 0 ? "bg-white" : "bg-slate-50/40",
                        isBusy && "opacity-60 pointer-events-none"
                      )}
                    >
                      <td className="px-3 py-3 align-top font-bold text-slate-500">
                        {idx + 1}
                      </td>

                      <td className="px-3 py-3 align-top">
                        <div className="font-black text-slate-900">
                          {r.officeName || "—"}
                        </div>
                        <div className="mt-1 text-[11px] text-slate-500">
                          State: {r.state || "—"}
                        </div>
                        {r.centralAgencyName && (
                          <div className="text-[10px] text-slate-400">
                            Agency: {r.centralAgencyName}
                          </div>
                        )}
                        <div className="mt-1 font-mono text-[10px] text-slate-400">
                          {r.id}
                        </div>
                      </td>

                      <td className="px-3 py-3 align-top text-[11px] text-slate-700">
                        <div className="font-semibold">{r.fullName || "—"}</div>
                        <div className="text-sky-700">{r.email || "—"}</div>
                        <div className="text-[10px] text-slate-500">{r.phone || "—"}</div>
                      </td>

                      <td className="px-3 py-3 align-top">
                        <Badge color="blue">{typeLabel}</Badge>
                      </td>

                      <td className="px-3 py-3 align-top text-[11px] text-slate-700 max-w-[180px]">
                        {Array.isArray(r.requestedDistricts) && r.requestedDistricts.length
                          ? r.requestedDistricts.join(", ")
                          : "—"}
                      </td>

                      <td className="px-3 py-3 align-top text-[11px] text-slate-700 max-w-[180px]">
                        {Array.isArray(r.assignedDistricts) && r.assignedDistricts.length ? (
                          <>
                            <div>{r.assignedDistricts.join(", ")}</div>
                            <button
                              type="button"
                              onClick={() => updateDistricts(r)}
                              className="mt-1 text-[10px] text-emerald-700 underline"
                            >
                              Edit Districts
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            onClick={() => updateDistricts(r)}
                            className="text-[10px] text-emerald-700 underline"
                          >
                            Set Districts
                          </button>
                        )}
                      </td>

                      <td className="px-3 py-3 text-center align-top">
                        <Badge color={color}>{status}</Badge>
                        {r.adminRemark && (
                          <div className="mt-1 text-[10px] text-slate-500 max-w-[140px]">
                            {r.adminRemark}
                          </div>
                        )}
                      </td>

                      <td className="px-3 py-3 text-center align-top">
                        {r.locked ? (
                          <Badge color="red">LOCKED</Badge>
                        ) : (
                          <Badge color="green">UNLOCKED</Badge>
                        )}
                      </td>

                      <td className="px-3 py-3 align-top">
                        <div className="flex flex-col gap-1 min-w-[120px]">
                          {status !== "approved" && (
                            <button
                              type="button"
                              onClick={() => approve(r)}
                              className="px-3 py-1 rounded-lg bg-emerald-600 text-white text-[11px] hover:bg-emerald-700"
                            >
                              Approve
                            </button>
                          )}

                          {status !== "rejected" && (
                            <button
                              type="button"
                              onClick={() => reject(r)}
                              className="px-3 py-1 rounded-lg bg-red-600 text-white text-[11px] hover:bg-red-700"
                            >
                              Reject
                            </button>
                          )}

                          <button
                            type="button"
                            onClick={() => openEdit(r)}
                            className="px-3 py-1 rounded-lg bg-slate-900 text-white text-[11px] hover:bg-slate-800"
                          >
                            Edit
                          </button>

                          <button
                            type="button"
                            onClick={() => toggleLock(r)}
                            className={cx(
                              "px-3 py-1 rounded-lg text-[11px]",
                              r.locked
                                ? "bg-emerald-50 text-emerald-700 border border-emerald-300"
                                : "bg-amber-50 text-amber-700 border border-amber-300"
                            )}
                          >
                            {r.locked ? "Unlock" : "Lock"}
                          </button>

                          <button
                            type="button"
                            onClick={() => handleDelete(r)}
                            className="px-3 py-1 rounded-lg bg-red-50 text-red-700 border border-red-200 text-[11px] hover:bg-red-100"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* EDIT MODAL */}
      {editing && editForm && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-6 px-3"
          onClick={closeEdit}
        >
          <div
            className="w-full max-w-4xl rounded-3xl bg-white shadow-2xl border border-slate-200 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-slate-950 via-emerald-950 to-teal-800 px-5 py-4 text-white flex items-center justify-between">
              <div>
                <div className="text-[11px] font-black uppercase tracking-[0.2em] text-emerald-300">
                  Edit Stakeholder Registration
                </div>
                <div className="mt-1 text-lg font-black">
                  {editing.officeName || "Agency Registration"}
                </div>
              </div>

              <button
                type="button"
                onClick={closeEdit}
                className="rounded-full bg-white/10 px-4 py-2 text-xs font-black hover:bg-white/20"
              >
                Close
              </button>
            </div>

            <form onSubmit={saveEdit} className="p-5 space-y-4 text-xs">
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Office Name">
                  <input
                    value={editForm.officeName}
                    onChange={(e) => updateEditField("officeName", e.target.value)}
                    className="w-full rounded-xl border px-3 py-2"
                  />
                </Field>

                <Field label="Office Type">
                  <select
                    value={editForm.officeType}
                    onChange={(e) => updateEditField("officeType", e.target.value)}
                    className="w-full rounded-xl border px-3 py-2"
                  >
                    <option value="">Select</option>
                    {OFFICE_TYPE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </Field>

                <Field label="Central / Nodal Agency Name">
                  <input
                    value={editForm.centralAgencyName}
                    onChange={(e) => updateEditField("centralAgencyName", e.target.value)}
                    className="w-full rounded-xl border px-3 py-2"
                  />
                </Field>

                <Field label="State">
                  <input
                    value={editForm.state}
                    onChange={(e) => updateEditField("state", e.target.value)}
                    className="w-full rounded-xl border px-3 py-2"
                  />
                </Field>

                <Field label="Contact Person">
                  <input
                    value={editForm.fullName}
                    onChange={(e) => updateEditField("fullName", e.target.value)}
                    className="w-full rounded-xl border px-3 py-2"
                  />
                </Field>

                <Field label="Mobile">
                  <input
                    value={editForm.phone}
                    onChange={(e) => updateEditField("phone", e.target.value)}
                    className="w-full rounded-xl border px-3 py-2"
                  />
                </Field>

                <Field label="Email">
                  <input
                    value={editForm.email}
                    onChange={(e) => updateEditField("email", e.target.value)}
                    className="w-full rounded-xl border px-3 py-2"
                  />
                </Field>

                <Field label="Status">
                  <select
                    value={editForm.status}
                    onChange={(e) => updateEditField("status", e.target.value)}
                    className="w-full rounded-xl border px-3 py-2"
                  >
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </Field>
              </div>

              <Field label="Requested Districts (comma separated)">
                <textarea
                  rows={2}
                  value={editForm.requestedDistrictsText}
                  onChange={(e) => updateEditField("requestedDistrictsText", e.target.value)}
                  className="w-full rounded-xl border px-3 py-2"
                />
              </Field>

              <Field label="Assigned Districts (comma separated)">
                <textarea
                  rows={2}
                  value={editForm.assignedDistrictsText}
                  onChange={(e) => updateEditField("assignedDistrictsText", e.target.value)}
                  className="w-full rounded-xl border px-3 py-2"
                />
              </Field>

              <Field label="Admin Remark">
                <textarea
                  rows={3}
                  value={editForm.adminRemark}
                  onChange={(e) => updateEditField("adminRemark", e.target.value)}
                  className="w-full rounded-xl border px-3 py-2"
                />
              </Field>

              <label className="flex items-center gap-2 rounded-2xl border bg-slate-50 p-3 text-sm font-bold text-slate-700">
                <input
                  type="checkbox"
                  checked={!!editForm.locked}
                  onChange={(e) => updateEditField("locked", e.target.checked)}
                />
                Lock this stakeholder login
              </label>

              <div className="flex justify-between pt-3">
                <button
                  type="button"
                  onClick={closeEdit}
                  disabled={savingEdit}
                  className="rounded-full border border-slate-300 px-5 py-2 text-xs font-black text-slate-600 hover:bg-slate-50"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={savingEdit}
                  className={cx(
                    "rounded-full px-6 py-2 text-xs font-black text-white shadow",
                    savingEdit
                      ? "bg-emerald-300 cursor-not-allowed"
                      : "bg-emerald-600 hover:bg-emerald-700"
                  )}
                >
                  {savingEdit ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}